/**
 * Display helper for text that came from a file or from the user (library paths, string arguments, wallet errors). It is always
 * rendered as React text (never as HTML); this only makes invisible and direction-changing characters visible, so a name cannot
 * look like something else in the review (a right-to-left override, a zero-width space, a control character).
 */
const RANGES: Array<[number, number]> = [
  [0x0000, 0x0008],
  [0x000b, 0x000c],
  [0x000e, 0x001f],
  [0x007f, 0x009f],
  [0x00ad, 0x00ad],
  [0x061c, 0x061c],
  [0x115f, 0x1160],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x206f],
  [0x3164, 0x3164],
  [0xfe00, 0xfe0f],
  [0xfeff, 0xfeff],
  [0xfff9, 0xfffb],
]
const isInvisible = (c: number) => RANGES.some(([a, b]) => c >= a && c <= b)

export function hasInvisible(s: string): boolean {
  for (let i = 0; i < s.length; i++) if (isInvisible(s.charCodeAt(i))) return true
  return false
}

/** Replaces each invisible / bidi / control character with a visible \u{XXXX} marker. */
export function visible(s: string, max = 4000): string {
  let out = ''
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    out += isInvisible(c) ? `\\u{${c.toString(16).toUpperCase().padStart(4, '0')}}` : s[i]
  }
  return out.length > max ? `${out.slice(0, max)}... (${out.length - max} more characters)` : out
}

/** A short, single-line, visible message from an error of any shape (wallet errors carry nested objects). */
export function errorText(e: unknown): string {
  const anyE = e as { shortMessage?: unknown; message?: unknown; code?: unknown } | null
  const raw = typeof anyE?.shortMessage === 'string' ? anyE.shortMessage : typeof anyE?.message === 'string' ? anyE.message : String(e)
  const code = anyE && (anyE.code === 4001 || anyE.code === 'ACTION_REJECTED') ? 'Rejected in the wallet. ' : ''
  return visible(`${code}${raw.split('\n')[0]}`, 300)
}
